import React, { useState } from "react";
import { api } from "@/lib/api";
import { useStores } from "@/lib/stores";
import { useAuth } from "@/lib/auth";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { Store, Plus, UserPlus, MapPin, Phone, Trash2, Lock, CreditCard, CheckCircle2, XCircle, Percent, Home, ShieldCheck } from "lucide-react";

export default function AdminStores() {
  const { stores, refresh } = useStores();
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const isCEO = isSuper && Array.isArray(user?.tags) && user.tags.includes("ceo");
  const [newStore, setNewStore] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", address: "", phone: "" });
  const [managing, setManaging] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "" });
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mpStatus, setMpStatus] = useState({}); // { store_id: {connected, commission_percent, mp_user_id, configured} }
  const [commissionEdits, setCommissionEdits] = useState({}); // { store_id: "10" }

  const loadMpStatus = React.useCallback(async () => {
    if (!stores?.length) return;
    const map = {};
    await Promise.all(stores.map(async (s) => {
      try {
        const { data } = await api.get(`/admin/stores/${s.store_id}/mp/status`);
        map[s.store_id] = data;
      } catch (err) {
        // Non-critical: MP status is decorative. Log for diagnostics, skip the row.
        if (err?.response?.status !== 401) logError(`[mp-status] store=${s.store_id}:`, err);
      }
    }));
    setMpStatus(map);
  }, [stores]);

  React.useEffect(() => { loadMpStatus(); }, [loadMpStatus]);

  // Handle post-OAuth redirect from Mercado Pago
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mp_connected")) {
      toast.success("Conta Mercado Pago conectada com sucesso!");
      window.history.replaceState({}, "", "/admin/lojas");
      loadMpStatus();
    } else if (params.get("mp_error")) {
      const err = params.get("mp_error");
      const detail = params.get("mp_detail") || "";
      const map = {
        access_denied: "Você (ou o franqueado) cancelou a autorização no Mercado Pago.",
        invalid_state: "Sessão expirou (>10min). Clique em \"Conectar\" de novo pra recomeçar.",
        redirect_mismatch: "A Redirect URI não está registrada no painel MP Developers. Entre em Aplicações → sua app → Detalhes e adicione: https://tech-assist-hub-21.emergent.host/api/mp/oauth/callback",
        oauth_failed: "Falha ao trocar o código pelo token do MP. Verifique Client ID/Secret e a Redirect URI no painel MP.",
        missing_params: "O Mercado Pago não retornou os parâmetros esperados. Tente de novo.",
      };
      toast.error(map[err] || `Falha ao conectar MP: ${err}${detail ? ` — ${detail}` : ""}`, { duration: 12000 });
      window.history.replaceState({}, "", "/admin/lojas");
    }
  }, [loadMpStatus]);

  const connectMp = async (storeId) => {
    try {
      const { data } = await api.get(`/admin/stores/${storeId}/mp/connect-url`);
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar OAuth"); }
  };

  const disconnectMp = async (storeId) => {
    if (!window.confirm("Desconectar a conta Mercado Pago desta loja? Novos pagamentos passarão a usar a conta padrão da plataforma.")) return;
    try {
      await api.post(`/admin/stores/${storeId}/mp/disconnect`);
      toast.success("Conta MP desconectada.");
      loadMpStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao desconectar"); }
  };

  const saveCommission = async (storeId) => {
    const raw = commissionEdits[storeId];
    const pct = parseFloat(String(raw).replace(",", "."));
    if (isNaN(pct) || pct < 0 || pct > 50) return toast.error("Comissão deve estar entre 0 e 50%");
    try {
      await api.patch(`/admin/stores/${storeId}`, { commission_percent: pct });
      toast.success(`Comissão salva: ${pct}%`);
      setCommissionEdits((s) => ({ ...s, [storeId]: undefined }));
      loadMpStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const toggleOwnerStore = async (storeId, next) => {
    try {
      await api.patch(`/admin/stores/${storeId}`, { is_owner_store: next });
      toast.success(next ? "Marcada como \"minha loja\" — 0% de comissão" : "Loja franqueada — comissão ativa");
      loadMpStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar"); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/stores", { ...form, code: form.code.toUpperCase() });
      toast.success("Loja criada!");
      setNewStore(false);
      setForm({ name: "", code: "", address: "", phone: "" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const openManage = async (store) => {
    setManaging(store);
    const { data } = await api.get(`/admin/stores/${store.store_id}/admins`);
    setAdmins(data);
  };

  const addAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password) return toast.error("Preencha e-mail e senha");
    try {
      await api.post(`/admin/stores/${managing.store_id}/admins`, newAdmin);
      toast.success("Admin criado.");
      setNewAdmin({ name: "", email: "", password: "" });
      const { data } = await api.get(`/admin/stores/${managing.store_id}/admins`);
      setAdmins(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete(`/admin/stores/${deleting.store_id}`);
      toast.success(`Loja "${deleting.name}" excluída.`);
      setDeleting(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Rede</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Lojas</h1>
          <p className="mt-2 text-sm text-neutral-600">Cada loja tem prefixo próprio de OS (ex: <span className="font-mono">SP01-XXXXX</span>) e um ou mais administradores.</p>
        </div>
        {isCEO ? (
          <Button onClick={() => setNewStore(true)} className="bg-neutral-900 hover:bg-neutral-800" data-testid="new-store"><Plus size={14} className="mr-1.5"/> Nova loja</Button>
        ) : (
          <div className="text-xs flex items-center gap-2 text-neutral-500 bg-neutral-100 px-3 py-2 rounded-lg" data-testid="stores-restricted">
            <Lock size={12}/> Apenas o CEO pode criar, editar ou configurar lojas
          </div>
        )}
      </div>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map((s) => (
          <div key={s.store_id} className="bg-white border border-neutral-200 rounded-xl p-5 hover:border-neutral-900 hover:-translate-y-0.5 transition-all group" data-testid={`store-${s.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-neutral-900 text-white rounded-lg grid place-items-center"><Store size={16}/></div>
                  <div>
                    <div className="font-heading font-bold">{s.name}</div>
                    <div className="font-mono text-xs text-neutral-500">Prefixo {s.code}-XXXXX</div>
                  </div>
                </div>
              </div>
              {s.store_id === "default" && <span className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-100 px-2 py-1 rounded">padrão</span>}
            </div>
            {mpStatus[s.store_id]?.is_owner_store && (
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`owner-badge-${s.code}`}>
                <ShieldCheck size={10}/> Minha loja · 0% comissão
              </div>
            )}
            {s.address && <div className="mt-3 text-sm text-neutral-600 flex items-center gap-1.5"><MapPin size={12}/> {s.address}</div>}
            {s.phone && <div className="mt-1 text-sm text-neutral-600 flex items-center gap-1.5"><Phone size={12}/> {s.phone}</div>}

            {/* Mercado Pago Marketplace */}
            {isCEO && (
              <div className="mt-4 pt-4 border-t border-neutral-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 flex items-center gap-1.5"><CreditCard size={11}/> Mercado Pago</div>
                  {mpStatus[s.store_id]?.connected ? (
                    <span className="text-[10px] flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold" data-testid={`mp-status-${s.code}`}><CheckCircle2 size={10}/> Conectado</span>
                  ) : (
                    <span className="text-[10px] flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-semibold" data-testid={`mp-status-${s.code}`}><XCircle size={10}/> Não conectado</span>
                  )}
                </div>
                {mpStatus[s.store_id]?.connected && (
                  <div className="text-[11px] text-neutral-500 font-mono mb-2">MP User ID: {mpStatus[s.store_id]?.mp_user_id}</div>
                )}

                {/* Owner toggle */}
                <button
                  type="button"
                  onClick={() => toggleOwnerStore(s.store_id, !mpStatus[s.store_id]?.is_owner_store)}
                  className={`w-full flex items-start gap-2 p-2 rounded-lg border text-left mb-2 transition-colors ${mpStatus[s.store_id]?.is_owner_store ? "border-emerald-300 bg-emerald-50" : "border-neutral-200 bg-white hover:border-neutral-400"}`}
                  data-testid={`toggle-owner-${s.code}`}
                >
                  <Home size={14} className={`mt-0.5 ${mpStatus[s.store_id]?.is_owner_store ? "text-emerald-700" : "text-neutral-500"}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">{mpStatus[s.store_id]?.is_owner_store ? "Esta é minha loja" : "Marcar como \"minha loja\""}</div>
                    <div className="text-[10px] text-neutral-500 leading-snug">{mpStatus[s.store_id]?.is_owner_store ? "Dinheiro cai 100% na conta MP conectada — sem retenção de comissão." : "Sem comissão da matriz — dinheiro cai 100% na conta MP conectada."}</div>
                  </div>
                </button>

                {!mpStatus[s.store_id]?.is_owner_store && (
                  <div className="flex items-end gap-2 mb-2">
                    <div className="flex-1">
                      <Label className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><Percent size={9}/> Comissão da matriz</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="50"
                        value={commissionEdits[s.store_id] ?? (mpStatus[s.store_id]?.commission_percent ?? 0)}
                        onChange={(e) => setCommissionEdits((st) => ({ ...st, [s.store_id]: e.target.value }))}
                        className="mt-1 h-9 font-mono text-sm"
                        data-testid={`commission-input-${s.code}`}
                      />
                    </div>
                    <Button size="sm" variant="outline" className="h-9" onClick={() => saveCommission(s.store_id)} data-testid={`save-commission-${s.code}`}>Salvar</Button>
                  </div>
                )}
                {mpStatus[s.store_id]?.connected ? (
                  <Button size="sm" variant="outline" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200" onClick={() => disconnectMp(s.store_id)} data-testid={`mp-disconnect-${s.code}`}>Desconectar MP</Button>
                ) : (
                  <Button size="sm" className="w-full bg-[#00b1ea] hover:bg-[#0091c4] text-white" onClick={() => connectMp(s.store_id)} disabled={!mpStatus[s.store_id]?.configured} data-testid={`mp-connect-${s.code}`}>
                    {mpStatus[s.store_id]?.configured === false ? "MP não configurado" : "Conectar Mercado Pago"}
                  </Button>
                )}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => openManage(s)} data-testid={`manage-${s.code}`}>
                <UserPlus size={12} className="mr-1.5"/> Administradores
              </Button>
              {isCEO && s.store_id !== "default" && (
                <Button size="sm" variant="outline" onClick={() => setDeleting(s)} className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200" data-testid={`delete-${s.code}`} title="Excluir loja">
                  <Trash2 size={14}/>
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* New store dialog */}
      <Dialog open={newStore} onOpenChange={setNewStore}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Nova loja</DialogTitle>
            <DialogDescription className="text-neutral-500 text-sm">Preencha os dados da nova filial. O prefixo aparecerá no início do código de cada OS gerada nesta loja.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" data-testid="store-name" /></div>
            <div><Label>Prefixo (2–8 letras/números)</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={8} className="mt-1.5 h-11 font-mono uppercase" placeholder="Ex: SP01, MTZ" data-testid="store-code" /></div>
            <div><Label>Endereço</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 h-11" data-testid="store-address" /></div>
            <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-11 font-mono" data-testid="store-phone" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewStore(false)}>Cancelar</Button>
            <Button onClick={create} disabled={busy || !form.name || !form.code} className="bg-neutral-900 hover:bg-neutral-800" data-testid="store-create">{busy ? "Criando…" : "Criar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Excluir loja</DialogTitle>
            <DialogDescription className="pt-2 text-neutral-700">
              Tem certeza que deseja excluir <b>{deleting?.name}</b> (prefixo <span className="font-mono">{deleting?.code}</span>)?
              <br/><br/>
              Esta ação é <b>irreversível</b>. A loja só pode ser excluída se não tiver OS nem administradores vinculados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            <Button onClick={confirmDelete} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="store-delete-confirm">{busy ? "Excluindo…" : "Sim, excluir"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage admins dialog */}
      <Dialog open={!!managing} onOpenChange={(v) => !v && setManaging(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Administradores — {managing?.name}</DialogTitle>
            <DialogDescription className="text-neutral-500 text-sm">Gerencie quem pode acessar o painel desta loja. Apenas o superadmin pode adicionar novos administradores.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
              {admins.length === 0 && <div className="p-4 text-sm text-neutral-500">Nenhum administrador nesta loja ainda.</div>}
              {admins.map((a) => (
                <div key={a.user_id} className="p-3 flex justify-between items-center text-sm">
                  <div>
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-xs text-neutral-500">{a.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">admin</span>
                </div>
              ))}
            </div>
            {isCEO && (
              <div className="border-t border-neutral-100 pt-4">
                <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2">Adicionar admin</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nome" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} className="col-span-2 h-10" data-testid="new-admin-name" />
                  <Input placeholder="E-mail" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} className="h-10" data-testid="new-admin-email" />
                  <PasswordInput placeholder="Senha" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} className="h-10" data-testid="new-admin-password" />
                </div>
                <Button onClick={addAdmin} className="w-full mt-3 bg-neutral-900 hover:bg-neutral-800" data-testid="new-admin-submit">Adicionar admin</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
